
import {
    defineStore,
    acceptHMRUpdate } from 'pinia';
import values from 'lodash/values';
import { useStarsStore } from '@/stores/stars.store';
import { fetchPages,
    fetchPage,
    updatePage,
    patchPage,
    createPage,
    deletePage } from '@/stores/api/pages.api';
import { createFrozenMap,
    appendKindIfNotProvided } from '@/stores/helpers/store.utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/stores/constants/user-settings-history.constants';
import {
    formatCrossAppDisplayName,
} from '@/stores/helpers/apps.helper';
import { isCrossApp } from '@/common/cross-app-countables/cross-app-countables.utils';
import { useHistoryStore } from '@/stores/history.store';
import { filterAnalyticsPages, type AnalyticsPagesOptions } from '@/stores/helpers/pages.helper';

interface Page {
    id: string;
    name?: string;
    appId?: string;
    appIds?: string[];
    kind?: string;
    dirty?: boolean;
    isCoreEvent?: boolean;
    excludeFromAnalytics?: boolean;
    icon?: { type: string };
    group?: unknown;
    rules?: Array<{ parsedRule: string }>;
    eventPropertyConfigurations?: unknown;
    eventPropertyNameList?: unknown;
    [key: string]: unknown;
}

interface PagesState {
    error: unknown;
    fetching: boolean;
    map: Record<string, Page>;
    isPagesBulkDeleteRunning: boolean;
}

function getInitialState (): PagesState {
    return {
        error: {} as unknown,
        fetching: false,
        map: {},
        isPagesBulkDeleteRunning: false,
    };
}

let loadAllPromise: Promise<void> | null = null;

export const usePagesStore = defineStore('pages', {
    state: getInitialState,
    getters: {
        list: (state): Page[] => {
            return values(state.map).filter((feature) => !isCrossApp(feature));
        },
        analyticsPagesList (): Page[] {
            return filterAnalyticsPages(this.list as Page[]);
        },
        pageById: (state) => (id: string): Page | undefined => state.map[id],
        pagesByAppId (): (appId: string) => Page[] {
            return (appId) => (this.list as Page[]).filter((page) => page.appId === appId);
        },
        analyticsPagesByAppId (): (appId: string, options?: AnalyticsPagesOptions) => Page[] {
            return (appId, options) => filterAnalyticsPages(this.pagesByAppId(appId) as Page[], options);
        },
        pagesByUrl (): (normalizedUrl: string) => Page[] {
            return (normalizedUrl) => (this.list as Page[]).filter((page) => {
                return page.rules?.some((rule) => {
                    const regex = new RegExp(rule.parsedRule);

                    return regex.test(normalizedUrl);
                });
            });
        },
        mapPagesWithStars (): (pages: Page[]) => Page[] {
            return (pages) => {
                const isStarred = useStarsStore().getStarByItemId;
                // eslint-disable-next-line @typescript-eslint/no-require-imports
                const { useAppsStore } = require('@/stores/apps.store');
                const appsStore = useAppsStore();

                return pages.map((page) =>
                    Object.freeze({
                        id: page.id,
                        name: page.name,
                        group: page.group,
                        appId: page.appId,
                        appIds: page.appIds,
                        appIcon: appsStore.getIconByAppId(page.appId),
                        kind: page.kind,
                        dirty: page.dirty,
                        isCoreEvent: page.isCoreEvent,
                        isStarred: !!isStarred(page.id),
                        icon: page.icon || { type: 'page' },
                        eventPropertyConfigurations: page.eventPropertyConfigurations,
                        eventPropertyNameList: page.eventPropertyNameList,
                    }) as Page,
                );
            };
        },
        pagesListWithStars (): Page[] {
            return this.mapPagesWithStars(this.list as Page[]);
        },
        analyticsPagesListWithStars (): Page[] {
            return this.mapPagesWithStars(this.analyticsPagesList as Page[]);
        },
        recentPagesInReporting (state): Page[] {
            const isStarred = useStarsStore().getStarByItemId;
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');
            const appsStore = useAppsStore();

            return useHistoryStore().itemHistory(
                RECENTLY_SELECTED_HISTORY_TYPES.PAGE_RECENTLY_SELECTED,
            )
                .filter(({ id }: { id: string }) => state.map[id]
                    && !isCrossApp(state.map[id])
                    && !state.map[id].excludeFromAnalytics)
                .map(({ id, timestamp }: { id: string; timestamp: number }) => {
                    const page = state.map[id];

                    return {
                        id: page.id,
                        name: page.name,
                        group: page.group,
                        appId: page.appId,
                        appIds: page.appIds,
                        appIcon: appsStore.getIconByAppId(page.appId),
                        appDisplayName: formatCrossAppDisplayName(page, appsStore.map),
                        kind: page.kind,
                        dirty: page.dirty,
                        isCoreEvent: page.isCoreEvent,
                        isStarred: !!isStarred(page.id),
                        icon: page.icon || { type: 'page' },
                        eventPropertyConfigurations: page.eventPropertyConfigurations,
                        eventPropertyNameList: page.eventPropertyNameList,
                        lastUsed: timestamp,
                    };
                });
        },
    },
    actions: {
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            this.fetching = true;
            loadAllPromise = fetchPages().then(({ data: pages }: { data: Page[] }) => {
                const pagesWithKind = pages.map((page) => appendKindIfNotProvided(page, 'Page'));
                this.fetching = false;
                this.map = createFrozenMap(pagesWithKind) as Record<string, Page>;
            }).catch((error: unknown) => {
                loadAllPromise = null;
                this.fetching = false;
                this.error = error;
                throw error;
            });

            await loadAllPromise;
        },
        loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string; [key: string]: unknown }) {
            if (!reFetch && this.pageById(id)) return Promise.resolve();

            this.fetching = true;

            return fetchPage(id)
                .then(({ data: page }: { data: Page }) => {
                    appendKindIfNotProvided(page, 'Page');
                    this.fetching = false;
                    this.map[page.id] = Object.freeze(page) as Page;
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        create ({ shouldStartProcessing = true, ...payload }: { shouldStartProcessing?: boolean; [key: string]: unknown } = {}) {
            this.fetching = true;

            return createPage(payload, shouldStartProcessing)
                .then(({ data: page }: { data: Page }) => {
                    this.fetching = false;
                    this.map[page.id] = Object.freeze(page) as Page;
                    // eslint-disable-next-line no-use-before-define
                    useSuggestedPagesStore().fetchSubscriptionPageRules();

                    return page;
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        update ({ shouldStartProcessing = false, ...page }: { shouldStartProcessing?: boolean; id: string; [key: string]: unknown }) {
            const existingPage = this.pageById(page.id as string);
            const existingPageWithNewRules = { ...existingPage, ...page };
            this.fetching = true;

            return updatePage(existingPageWithNewRules, shouldStartProcessing)
                .then(({ data: savedPage }: { data: Page }) => {
                    this.fetching = false;
                    this.map[savedPage.id] = Object.freeze(savedPage) as Page;
                    // eslint-disable-next-line no-use-before-define
                    useSuggestedPagesStore().fetchSubscriptionPageRules();

                    return savedPage;
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async setExcludeFromAnalytics ({ id, excludeFromAnalytics }: { id: string; excludeFromAnalytics: boolean }) {
            this.fetching = true;

            try {
                const { data: savedPage }: { data?: Page } = await patchPage(id, [
                    { op: 'replace', path: '/excludeFromAnalytics', value: excludeFromAnalytics },
                ]);
                const updatedPage = { ...this.pageById(id), ...savedPage, excludeFromAnalytics };
                this.map[id] = Object.freeze(updatedPage) as Page;

                return updatedPage;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        remove (payload: { id: string; [key: string]: unknown }) {
            this.fetching = true;

            return deletePage(payload)
                .then(() => {
                    this.fetching = false;
                    delete this.map[payload.id];
                    // eslint-disable-next-line no-use-before-define
                    useSuggestedPagesStore().fetchSubscriptionPageRules();
                })
                .catch((error: unknown) => {
                    this.fetching = false;
                    this.error = error;
                    throw error;
                });
        },
        async assignGroup (payload: { page: Page; group: { id: string; [key: string]: unknown } }) {
            const { page, group } = payload;
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useGroupsStore } = require('@/stores/groups.store');
            const groupPayload = {
                groupId: group.id,
                itemId: page.id,
                itemType: 'page',
            };

            this.fetching = true;

            try {
                await useGroupsStore().addItemToGroup(groupPayload);

                const updatedPage = { ...page, group };
                this.map[page.id] = Object.freeze(updatedPage) as Page;

                return page;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        setIsPagesBulkDeleteRunning (isPagesBulkDeleteRunning: boolean) {
            this.isPagesBulkDeleteRunning = isPagesBulkDeleteRunning;
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

// Imported after usePagesStore to avoid circular dependency
import { useSuggestedPagesStore } from './suggested-pages.store';

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(usePagesStore, module.hot));
}
